import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  numero,
  moneda,
  fecha,
  centro,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte Insumos Y Proveedores.
 *
 * Una sola tabla, armada una vez, alimenta los dos archivos para que no puedan
 * decir cosas distintas (mismo patron que `exportarCostoVsUltimaCompra.js`).
 *
 * En pantalla la tabla va acotada porque `TableItem` comprime las columnas
 * (tableLayout fijo, sin scroll horizontal en escritorio); el detalle completo
 * del catalogo vive aqui.
 */

// Las dos unidades salen formateadas del backend como "CLAVE - Nombre" (misma
// convencion que ModalEditarRelacion): el costo esta en la del PROVEEDOR y el
// costo unitario en la de INVENTARIO. Las columnas crudas de las que se derivan
// (Unidad Informativa y Clave Unidad) se conservan mas abajo.
//
// Los `anchoPdf` de las columnas con `enPdf !== false` suman 732 pt y la hoja
// LETTER horizontal de `reporteFiscalPdf` da 748 utiles (792 menos los margenes
// de 22). Una fila mas ancha que `widths` truena pdfmake, asi que esa suma hay
// que rehacerla al mover cualquiera de estos numeros.
const COLUMNAS = [
  { titulo: "Codigo", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Clave", tipo: "texto", ancho: 16, anchoPdf: 52 },
  { titulo: "Descripcion Insumo", tipo: "texto", ancho: 42, anchoPdf: 118 },
  { titulo: "Familia", tipo: "texto", ancho: 20, enPdf: false },
  { titulo: "Marca", tipo: "texto", ancho: 18, enPdf: false },
  { titulo: "Unidad Inventario", tipo: "texto", ancho: 22, anchoPdf: 46 },
  { titulo: "Proveedor", tipo: "texto", ancho: 34, anchoPdf: 110 },
  { titulo: "Codigo Proveedor", tipo: "texto", ancho: 18, anchoPdf: 62 },
  { titulo: "Descripcion Proveedor", tipo: "texto", ancho: 40, enPdf: false },
  { titulo: "Unidad Proveedor", tipo: "texto", ancho: 22, anchoPdf: 46 },
  { titulo: "Costo", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "Moneda", tipo: "texto", ancho: 9, enPdf: false },
  { titulo: "Costo Sin Descuento", tipo: "moneda", ancho: 18, enPdf: false },
  { titulo: "Conversion", tipo: "numero", ancho: 11, anchoPdf: 46 },
  { titulo: "Costo Unitario", tipo: "moneda", ancho: 16, anchoPdf: 62 },
  { titulo: "Compra Minima", tipo: "numero", ancho: 13, enPdf: false },
  { titulo: "Unidad Informativa", tipo: "texto", ancho: 16, enPdf: false },
  { titulo: "Ultimo Cambio Costo", tipo: "fecha", ancho: 15, anchoPdf: 58 },
  { titulo: "Costo Anterior", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Clave Prod Serv", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Clave Unidad", tipo: "texto", ancho: 12, enPdf: false },
  { titulo: "Origen", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Marcas", tipo: "texto", ancho: 24, anchoPdf: 70 },
];

/**
 * Las banderas del catalogo, en una sola columna legible.
 *
 * `principal` es la relacion marcada como principal del insumo;
 * `esProveedorDefault` es el proveedor que usa el pedido automatico
 * (`Insumo.proveedorDefaultId`) — son dos cosas distintas y conviene verlas.
 */
export const etiquetaMarcas = (registro) => {
  if (registro.sinProveedor) return "Sin proveedor";
  const marcas = [];
  if (registro.esPrincipal) marcas.push("Principal");
  if (registro.esProveedorDefault) marcas.push("Proveedor default");
  if (!registro.costo) marcas.push("Sin costo");
  if (!registro.conversion) marcas.push("Sin conversion");
  return marcas.join(", ");
};

/** Una relacion capturada a mano vs. una sembrada por el match del CFDI. */
export const etiquetaOrigen = (registro) => {
  if (registro.sinProveedor) return "";
  return registro.capturaManual ? "Manual" : "XML";
};

export const construirTabla = (registros) => {
  const filas = (registros || []).map((registro) => {
    // El renglon sin proveedor es el que hay que corregir: se resalta en el PDF.
    const relleno = registro.sinProveedor ? "#FFCCC7" : null;
    const celdaProveedor = registro.sinProveedor
      ? centro("Sin proveedor")
      : texto(registro.proveedor);

    return [
      texto(registro.codigoInsumo),
      texto(registro.claveInsumo),
      texto(registro.descripcionInsumo),
      texto(registro.nombreFamilia),
      texto(registro.nombreMarca),
      texto(registro.unidadInventario),
      relleno ? { ...celdaProveedor, fillColor: relleno } : celdaProveedor,
      texto(registro.codigoProveedor),
      texto(registro.descripcionProveedor),
      texto(registro.unidadProveedor),
      moneda(registro.costo),
      texto(registro.abreviacionMoneda),
      moneda(registro.costoSinDescuento),
      numero(registro.conversion),
      moneda(registro.costoUnitario),
      numero(registro.compraMin),
      texto(registro.unidadInformativa),
      fecha(registro.fechaUltimoCambioCosto),
      moneda(registro.costoAnterior),
      texto(registro.claveProdServ),
      texto(registro.claveUnidad),
      texto(etiquetaOrigen(registro)),
      texto(etiquetaMarcas(registro)),
    ];
  });

  // Sin `totalizar`: todas las columnas de dinero de este reporte son COSTOS
  // UNITARIOS de insumos distintos (y en monedas distintas). Sumarlas no
  // significa nada. Con los totales en null, `construirHojaTabla` omite la fila.
  return { columnas: COLUMNAS, filas, totales: COLUMNAS.map(() => null) };
};

const TITULO = "Catalogo de insumos por proveedor";

const nombreArchivo = (empresaNombre) =>
  `Insumos_Y_Proveedores_${limpiarNombreArchivo(empresaNombre)}_${moment().format(
    "YYYY-MM-DD"
  )}`;

const indicadoresDe = (resumen = {}) => [
  { etiqueta: "Relaciones", valor: String(resumen.relaciones || 0) },
  { etiqueta: "Insumos", valor: String(resumen.insumos || 0) },
  { etiqueta: "Proveedores", valor: String(resumen.proveedores || 0) },
  { etiqueta: "Sin proveedor", valor: String(resumen.sinProveedor || 0) },
  { etiqueta: "Sin costo", valor: String(resumen.sinCosto || 0) },
];

export const descargarExcel = ({ datos, empresaNombre, descripcionFiltros }) => {
  descargarLibro(
    [
      {
        nombre: "Insumos Y Proveedores",
        hoja: construirHojaTabla(construirTabla(datos), {
          titulo: TITULO,
          empresaNombre,
          descripcionFiltros,
        }),
      },
    ],
    nombreArchivo(empresaNombre)
  );
};

export const descargarPdf = ({
  datos,
  resumen,
  empresaNombre,
  descripcionFiltros,
}) => {
  const tabla = construirTabla(datos);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: "Reporte Insumos Y Proveedores",
        empresaNombre,
        descripcionFiltros,
        indicadores: indicadoresDe(resumen),
        contador: tabla.filas.length,
        secciones: [{ titulo: TITULO, ...paraPdf(tabla) }],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
